// AttackToastStack — grouped toast for "your city was attacked!" alerts.
// Consecutive hits aggregate into ONE card (×count, total damage) that can
// expand to the per-hit detail list, and one ✕ clears everything. Fully
// driven by props (App owns the queue via useAttackToasts) so E2E can inject
// + read state through the test bridge.

import { Button } from '@aod/ui/components/ui/button';
import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { useState } from 'react';
import { useT } from '../i18n/index.js';
import { appDom } from '../lib/appDom.js';
import { attackToastScene } from './sceneEvents.js';
import { sceneUrls } from './scenes.js';

/** One recorded hit (oldest first). */
export interface AttackHit {
  readonly damage: number;
  readonly hpRemaining: number;
}

export interface AttackToast {
  readonly id: number;
  /** Number of aggregated hits — the ×N badge. */
  readonly count: number;
  /** Total damage across hits. */
  readonly damage: number;
  /** Latest HP remaining. */
  readonly hpRemaining: number;
  readonly hits: ReadonlyArray<AttackHit>;
}

interface AttackToastStackProps {
  readonly toasts: ReadonlyArray<AttackToast>;
  readonly onDismiss: (id: number) => void;
}

export function AttackToastStack({ toasts, onDismiss }: AttackToastStackProps) {
  const t = useT();
  const [expandedId, setExpandedId] = useState<number | null>(null);
  if (toasts.length === 0) return null;
  return (
    <output
      className="pointer-events-none fixed top-[calc(var(--hud-strip-top)+2.75rem)] left-1/2 z-50 flex -translate-x-1/2 flex-col items-center gap-2"
      data-testid="attack-toasts"
      {...appDom({ id: 'attack-toasts', type: 'Toast', scope: 'toast', label: t('toast.label') })}
      aria-live="polite"
      aria-label={t('toast.label')}
    >
      {toasts.map((toast) => {
        const expanded = expandedId === toast.id;
        return (
          <div
            key={toast.id}
            className="pointer-events-auto min-w-[16rem] rounded-md border border-rose-500/50 bg-panel-scrim px-3 py-2 text-[12px] text-foreground shadow-lg backdrop-blur-sm"
            data-testid="attack-toast"
          >
            <div className="flex items-center gap-2">
              <img
                src={sceneUrls(attackToastScene().group, attackToastScene().id).srcSmall}
                alt=""
                loading="lazy"
                className="h-10 w-16 shrink-0 rounded border border-rose-500/30 object-cover"
                data-testid="attack-toast-scene"
              />
              <span className="flex-1 font-medium">
                {t('toast.attacked')}
                {toast.count > 1 && (
                  <span
                    className="ml-1.5 rounded-sm bg-rose-500/20 px-1.5 py-0.5 text-[10px] font-bold text-rose-700 tabular-nums dark:text-rose-300"
                    data-testid="attack-toast-count"
                  >
                    ×{toast.count}
                  </span>
                )}{' '}
                <span className="text-rose-700 tabular-nums dark:text-rose-300">
                  {t('toast.damage', { damage: toast.damage })}
                </span>
                <span className="ml-1 text-muted-foreground tabular-nums">
                  {t('toast.remaining', { hp: toast.hpRemaining })}
                </span>
              </span>
              {toast.count > 1 && (
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  aria-label={expanded ? t('toast.collapse') : t('toast.expand')}
                  aria-expanded={expanded}
                  className="size-5"
                  data-testid="attack-toast-expand"
                  onClick={() => setExpandedId(expanded ? null : toast.id)}
                >
                  {expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                </Button>
              )}
              <Button
                type="button"
                size="icon"
                variant="ghost"
                aria-label={t('toast.dismiss')}
                className="size-6"
                data-testid="attack-toast-dismiss"
                onClick={() => {
                  setExpandedId(null);
                  onDismiss(toast.id);
                }}
              >
                <X className="size-3" />
              </Button>
            </div>
            {expanded && (
              <ul
                className="mt-1 space-y-0.5 border-t border-border/50 pt-1 font-mono text-[11px] text-muted-foreground"
                data-testid="attack-toast-hits"
              >
                {toast.hits.map((hit, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span className="text-rose-700 tabular-nums dark:text-rose-300">
                      {t('toast.damage', { damage: hit.damage })}
                    </span>
                    <span className="tabular-nums">
                      {t('toast.remaining', { hp: hit.hpRemaining })}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </output>
  );
}
